import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../common/Button';
import { DropdownMultiTypeahead } from '../../common/DropdownMultiTypeahead';
import { DropdownTypeahead } from '../../common/DropdownTypeahead';
import FormLanguageSelector from '../../common/FormLanguageSelector';
import { InputGroup } from '../../common/InputGroup';
import { RadioButton } from '../../common/RadioButton';
import { ToggleButton } from '../../common/ToggleButton';
import JobPreview from '../JobPreview';
import { useCitySelector } from '../../../hooks/useCitySelector';
import { useListingTypeSelector } from '../../../hooks/useListingTypeSelector';
import { useServiceCategorySelector } from '../../../hooks/useServiceCategorySelector';
import { useProfileStore } from '../../../stores/profileStore';
import { useJobStore } from '../../../stores/jobStore';
import { useCreditStore } from '../../../stores/creditStore';
import { useShadowRecruiterStore } from '../../../stores/shadowRecruiterStore';
import WalletSummary from '../../credits/WalletSummary';
import type { BudgetRange } from '../../../types/quote';
import type { JobUpsertParams } from '../../../types/job';
import type { City } from '../../../types/profile';
import {
  createPublicationFingerprint,
  discardJobCreationSession,
  type JobCreationSessionParams,
  readJobCreationSession,
  writeJobCreationSession,
} from './jobCreationSession';
import {
  toTorontoDateValue,
  validatePublicationDates,
  validateWorkDates,
} from './jobCreationDates';

export interface CreateJobWizardCompletion {
  params: JobUpsertParams;
  listingTypeId: number;
  expiresAt: string;
  selectionDate: string | null;
  jobId?: string;
  publicationRequestId: string;
  fingerprint: string;
  isShadowRecruiter: boolean;
  onDraftSaved: (jobId: string) => void;
}

interface CreateJobWizardProps {
  onComplete: (completion: CreateJobWizardCompletion) => Promise<void>;
  onCancel: () => void;
}

const blankParams: Omit<JobUpsertParams, 'city' | 'budgetRange' | 'requiredServices'> = {
  title: '',
  description: '',
  onSite: false,
  languages: ['fr'],
  startDate: '',
  endDate: null,
};

const makeRequestId = (): string => crypto.randomUUID();

type WizardField =
  | 'title'
  | 'description'
  | 'budgetRange'
  | 'services'
  | 'city'
  | 'languages'
  | 'startDate'
  | 'endDate'
  | 'expiresAt'
  | 'selectionDate'
  | 'listingType';

const CreateJobWizard: React.FC<CreateJobWizardProps> = ({ onComplete, onCancel }) => {
  const { t } = useTranslation(['jobs', 'common']);
  const { profile } = useProfileStore();
  const { budgetRanges, hasSettledBudgetRangesLoad, fetchBudgetRanges } = useJobStore();
  const { balance, refreshBalance } = useCreditStore();
  const {
    shadowRecruiters,
    loading: loadingShadowRecruiters,
    fetchShadowRecruiters,
  } = useShadowRecruiterStore();
  const {
    options: listingOptions,
    selectedOption,
    setSelectedOption,
    loading: loadingListingTypes,
    hasSettledOptionsLoad: hasSettledListingTypesLoad,
  } = useListingTypeSelector();
  const {
    selectedOptions: cities,
    setSelectedOptions: setCities,
    options: cityOptions,
    loading: loadingCities,
    hasSettledOptionsLoad: hasSettledCitiesLoad,
  } = useCitySelector(profile?.city ? [profile.city] : []);
  const {
    selectedOptions: services,
    setSelectedOptions: setServices,
    filteredOptions: serviceOptions,
    setSearchInput,
    loading: loadingServices,
    hasSettledOptionsLoad: hasSettledServicesLoad,
  } = useServiceCategorySelector();
  const [params, setParams] = useState(blankParams);
  const [budgetRange, setBudgetRange] = useState<BudgetRange | null>(null);
  const [step, setStep] = useState(0);
  const [expiresAt, setExpiresAt] = useState('');
  const [selectionDate, setSelectionDate] = useState('');
  const [shadowRecruiterId, setShadowRecruiterId] = useState('');
  const [jobId, setJobId] = useState<string | undefined>();
  const [publicationRequestId, setPublicationRequestId] = useState(makeRequestId);
  const [error, setError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<Set<WizardField>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [restorationComplete, setRestorationComplete] = useState(false);
  const hydratedRef = useRef(false);
  const restoredListingTypeIdRef = useRef<number | null | undefined>(undefined);
  const restoredCityIdRef = useRef<number | null | undefined>(undefined);
  const restoredBudgetRangeIdRef = useRef<number | null | undefined>(undefined);
  const restoredServiceIdsRef = useRef<number[] | undefined>(undefined);
  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const previousStepRef = useRef(step);
  const today = useMemo(() => toTorontoDateValue(new Date()), []);
  const userId = profile?.id;
  const isAdministrator = profile?.role === 'administrator';
  const selectedCity = cities[0] ?? null;

  useEffect(() => {
    void fetchBudgetRanges();
    void refreshBalance();
  }, [fetchBudgetRanges, refreshBalance]);
  useEffect(() => {
    if (isAdministrator) void fetchShadowRecruiters();
  }, [fetchShadowRecruiters, isAdministrator]);

  useEffect(() => {
    if (!userId || hydratedRef.current) return;
    const saved = readJobCreationSession(userId);
    hydratedRef.current = true;
    if (!saved) {
      setRestorationComplete(true);
      return;
    }
    setStep(saved.step);
    setParams({ ...saved.params, endDate: saved.params.endDate ?? null });
    restoredCityIdRef.current = saved.params.city?.id ?? null;
    restoredBudgetRangeIdRef.current = saved.params.budgetRange?.id ?? null;
    restoredServiceIdsRef.current = saved.params.requiredServices.map(service => service.id);
    setExpiresAt(saved.expiresAt);
    setSelectionDate(saved.selectionDate);
    restoredListingTypeIdRef.current = saved.listingTypeId;
    setShadowRecruiterId(saved.params.shadowRecruiterId ?? '');
    setJobId(saved.jobId ?? undefined);
    setPublicationRequestId(saved.publicationRequestId);
  }, [setCities, setServices, userId]);

  useEffect(() => {
    if (
      restoredListingTypeIdRef.current === undefined ||
      loadingListingTypes ||
      !hasSettledListingTypesLoad
    ) {
      return;
    }
    const savedListingTypeId = restoredListingTypeIdRef.current;
    setSelectedOption(
      savedListingTypeId === null
        ? null
        : (listingOptions.find(option => option.id === savedListingTypeId) ?? null)
    );
    restoredListingTypeIdRef.current = undefined;
  }, [hasSettledListingTypesLoad, listingOptions, loadingListingTypes, setSelectedOption]);

  useEffect(() => {
    if (restoredCityIdRef.current === undefined || loadingCities || !hasSettledCitiesLoad) {
      return;
    }
    const savedCityId = restoredCityIdRef.current;
    const restoredCity = cityOptions.find(city => city.id === savedCityId);
    setCities(restoredCity ? [restoredCity] : []);
    restoredCityIdRef.current = undefined;
  }, [cityOptions, hasSettledCitiesLoad, loadingCities, setCities]);

  useEffect(() => {
    if (restoredBudgetRangeIdRef.current === undefined || !hasSettledBudgetRangesLoad) {
      return;
    }
    const savedBudgetRangeId = restoredBudgetRangeIdRef.current;
    setBudgetRange(
      savedBudgetRangeId === null
        ? null
        : (budgetRanges.find(range => range.id === savedBudgetRangeId) ?? null)
    );
    restoredBudgetRangeIdRef.current = undefined;
  }, [budgetRanges, hasSettledBudgetRangesLoad]);

  useEffect(() => {
    if (restoredServiceIdsRef.current === undefined || loadingServices || !hasSettledServicesLoad) {
      return;
    }
    const savedServiceIds = restoredServiceIdsRef.current;
    const restoredServices = serviceOptions.filter(service => savedServiceIds.includes(service.id));
    setServices(restoredServices);
    restoredServiceIdsRef.current = undefined;
  }, [hasSettledServicesLoad, loadingServices, serviceOptions, setServices]);

  useEffect(() => {
    if (
      !hydratedRef.current ||
      restoredListingTypeIdRef.current !== undefined ||
      restoredCityIdRef.current !== undefined ||
      restoredBudgetRangeIdRef.current !== undefined ||
      restoredServiceIdsRef.current !== undefined ||
      !hasSettledListingTypesLoad ||
      !hasSettledCitiesLoad ||
      !hasSettledBudgetRangesLoad ||
      !hasSettledServicesLoad
    ) {
      return;
    }
    setRestorationComplete(true);
  }, [
    budgetRanges,
    cityOptions,
    listingOptions,
    loadingCities,
    loadingListingTypes,
    loadingServices,
    hasSettledBudgetRangesLoad,
    hasSettledCitiesLoad,
    hasSettledListingTypesLoad,
    hasSettledServicesLoad,
    serviceOptions,
  ]);

  const serializableParams = useMemo<JobUpsertParams | null>(
    () =>
      selectedCity && budgetRange
        ? {
            ...params,
            city: selectedCity,
            budgetRange,
            requiredServices: services,
            shadowRecruiterId: shadowRecruiterId || null,
          }
        : null,
    [budgetRange, params, selectedCity, services, shadowRecruiterId]
  );

  const sessionParams = useMemo<JobCreationSessionParams>(
    () => ({
      ...params,
      city: selectedCity,
      budgetRange,
      requiredServices: services,
      shadowRecruiterId: shadowRecruiterId || null,
    }),
    [budgetRange, params, selectedCity, services, shadowRecruiterId]
  );

  useEffect(() => {
    if (
      !userId ||
      !hydratedRef.current ||
      !restorationComplete ||
      restoredListingTypeIdRef.current !== undefined ||
      restoredCityIdRef.current !== undefined ||
      restoredBudgetRangeIdRef.current !== undefined ||
      restoredServiceIdsRef.current !== undefined
    ) {
      return;
    }
    writeJobCreationSession(userId, {
      version: 1,
      savedAt: Date.now(),
      step,
      params: sessionParams,
      expiresAt,
      selectionDate,
      listingTypeId: selectedOption?.id ?? null,
      jobId: jobId ?? null,
      fingerprint:
        jobId && selectedOption
          ? createPublicationFingerprint(jobId, selectedOption.id, expiresAt, selectionDate)
          : null,
      publicationRequestId,
    });
  }, [
    expiresAt,
    jobId,
    publicationRequestId,
    selectionDate,
    selectedOption,
    sessionParams,
    step,
    userId,
    restorationComplete,
  ]);

  const validationMessage = t('wizard.validation.complete_required_details', { ns: 'jobs' });
  const clearFieldError = (field: WizardField) => {
    setError(null);
    setInvalidFields(current => {
      if (!current.has(field)) return current;
      const next = new Set(current);
      next.delete(field);
      return next;
    });
  };
  const detailsValidation = (): { fields: WizardField[]; message: string | null } => {
    const fields: WizardField[] = [];
    if (!params.title.trim()) fields.push('title');
    if (!params.description.trim()) fields.push('description');
    if (!budgetRange) fields.push('budgetRange');
    if (services.length === 0) fields.push('services');
    if (!selectedCity) fields.push('city');
    if (params.languages.length === 0) fields.push('languages');
    return { fields, message: fields.length > 0 ? validationMessage : null };
  };
  const durationValidation = (): { fields: WizardField[]; message: string | null } => {
    const dates = validateWorkDates(params.startDate, params.endDate, today);
    if (dates.startDate || dates.endDate) {
      return {
        fields: [dates.startDate ? 'startDate' : 'endDate'],
        message: t(`wizard.validation.${dates.startDate ?? dates.endDate}`, { ns: 'jobs' }),
      };
    }
    const publication = validatePublicationDates(expiresAt, selectionDate, params.startDate, today);
    const dateError = publication.expiresAt ?? publication.selectionDate;
    return dateError
      ? {
          fields: [publication.expiresAt ? 'expiresAt' : 'selectionDate'],
          message: t(`wizard.validation.${dateError}`, { ns: 'jobs' }),
        }
      : { fields: [], message: null };
  };
  const next = () => {
    const validation =
      step === 0
        ? detailsValidation()
        : step === 1
          ? durationValidation()
          : step === 2 && !selectedOption
            ? {
                fields: ['listingType'] as WizardField[],
                message: t('wizard.validation.choose_listing_priority', { ns: 'jobs' }),
              }
            : { fields: [], message: null };
    if (validation.message) {
      setInvalidFields(new Set(validation.fields));
      setError(validation.message);
      return;
    }
    setInvalidFields(new Set());
    setError(null);
    setStep(current => Math.min(4, current + 1));
  };
  const complete = async () => {
    if (!serializableParams || !selectedOption) return;
    const validation = durationValidation();
    if (validation.message) {
      setInvalidFields(new Set(validation.fields));
      setError(validation.message);
      setStep(1);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = createPublicationFingerprint(
        jobId ?? 'pending',
        selectedOption.id,
        expiresAt,
        selectionDate
      );
      await onComplete({
        params: serializableParams,
        listingTypeId: selectedOption.id,
        expiresAt,
        selectionDate: selectionDate || null,
        jobId,
        publicationRequestId,
        fingerprint,
        isShadowRecruiter: Boolean(shadowRecruiterId),
        onDraftSaved: setJobId,
      });
      if (userId) discardJobCreationSession(userId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.generic', { ns: 'common' }));
    } finally {
      setSubmitting(false);
    }
  };
  const discard = () => {
    if (userId) discardJobCreationSession(userId);
    onCancel();
  };
  const stepName = t(
    `wizard.steps.${['details', 'duration', 'recruitment', 'review', 'payment'][step]}`,
    {
      ns: 'jobs',
    }
  );
  const isDraftOnly = isAdministrator && !shadowRecruiterId;

  useEffect(() => {
    if (previousStepRef.current !== step) stepHeadingRef.current?.focus();
    previousStepRef.current = step;
  }, [step]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <h3 ref={stepHeadingRef} tabIndex={-1} className="sr-only">
          {stepName}
        </h3>
        <p className="font-semibold text-secondary" aria-live="polite" aria-atomic="true">
          {t('wizard.progress', { ns: 'jobs', current: step + 1, total: 5, step: stepName })}
        </p>
        <div className="flex gap-1">
          {[0, 1, 2, 3, 4].map(index => (
            <span
              key={index}
              className={`h-1.5 w-8 rounded ${index <= step ? 'bg-primary' : 'bg-gray-200'}`}
            />
          ))}
        </div>
      </div>
      {step === 0 && (
        <div className="grid gap-4 md:grid-cols-2">
          <InputGroup
            label={t('form.project_name', { ns: 'jobs' })}
            name="job-title"
            value={params.title}
            onChange={event => {
              setParams(current => ({ ...current, title: event.target.value }));
              clearFieldError('title');
            }}
            required
            error={invalidFields.has('title')}
            errorMessage={validationMessage}
          />
          <InputGroup
            label={t('form.price_range', { ns: 'jobs' })}
            name="job-budget-range"
            required
            error={invalidFields.has('budgetRange')}
            errorMessage={validationMessage}
          >
            <div
              className="mt-2 grid grid-cols-2 gap-2"
              role="group"
              aria-invalid={invalidFields.has('budgetRange') || undefined}
              aria-describedby={
                invalidFields.has('budgetRange') ? 'job-budget-range-error' : undefined
              }
            >
              {budgetRanges.map(option => (
                <RadioButton
                  key={option.id}
                  name="budgetRange"
                  label={t(`budget_ranges.${option.code}`, { ns: 'common' })}
                  value={option.id}
                  checked={budgetRange?.id === option.id}
                  onChange={() => {
                    setBudgetRange(option);
                    clearFieldError('budgetRange');
                  }}
                />
              ))}
            </div>
          </InputGroup>
          <InputGroup
            label={t('service_type', { ns: 'jobs' })}
            name="job-services"
            required
            error={invalidFields.has('services')}
            errorMessage={validationMessage}
          >
            <DropdownMultiTypeahead
              selected={services}
              onChange={selectedServices => {
                setServices(selectedServices);
                clearFieldError('services');
              }}
              options={serviceOptions}
              onSearchChange={setSearchInput}
              getOptionLabel={item => item.label}
              getOptionValue={item => item.id}
              placeholder={t('wizard.select_services', { ns: 'jobs' })}
              className="mt-1"
              inputMode="search"
              error={invalidFields.has('services')}
              inputId="job-services"
              ariaDescribedBy={invalidFields.has('services') ? 'job-services-error' : undefined}
            />
          </InputGroup>
          <InputGroup
            label={t('form.location', { ns: 'jobs' })}
            name="job-city"
            required
            error={invalidFields.has('city')}
            errorMessage={validationMessage}
          >
            <DropdownTypeahead
              className="mt-2"
              value={selectedCity?.name ?? ''}
              onChange={(_value, item?: City) => {
                setCities(item ? [item] : []);
                clearFieldError('city');
              }}
              options={cityOptions}
              getOptionLabel={item => item.name}
              getOptionValue={item => item.id}
              placeholder={t('form.location_placeholder', { ns: 'jobs' })}
              showDropdownIcon={false}
              error={invalidFields.has('city')}
              inputId="job-city"
              ariaDescribedBy={invalidFields.has('city') ? 'job-city-error' : undefined}
            />
          </InputGroup>
          <InputGroup label={t('form.onsite', { ns: 'jobs' })}>
            <ToggleButton
              checked={params.onSite}
              onChange={event =>
                setParams(current => ({ ...current, onSite: event.target.checked }))
              }
              label={params.onSite ? t('common:form_fields.yes') : t('common:form_fields.no')}
              className="mt-2"
            />
          </InputGroup>
          <FormLanguageSelector
            value={params.languages}
            onChange={languages => {
              setParams(current => ({ ...current, languages }));
              clearFieldError('languages');
            }}
            required
            error={invalidFields.has('languages') ? validationMessage : undefined}
            name="job-languages"
          />
          <div className="md:col-span-2">
            <InputGroup
              label={t('form.description', { ns: 'jobs' })}
              name="job-description"
              as="textarea"
              value={params.description}
              onChange={event => {
                setParams(current => ({ ...current, description: event.target.value }));
                clearFieldError('description');
              }}
              required
              error={invalidFields.has('description')}
              errorMessage={validationMessage}
            />
          </div>
          {(loadingCities || loadingServices) && (
            <p className="text-sm text-secondary">{t('wizard.loading_choices', { ns: 'jobs' })}</p>
          )}
        </div>
      )}
      {step === 1 && (
        <div className="grid gap-4 md:grid-cols-2">
          <InputGroup
            type="date"
            label={t('form.start_date', { ns: 'jobs' })}
            name="job-start-date"
            value={params.startDate}
            min={today}
            onChange={event => {
              setParams(current => ({ ...current, startDate: event.target.value }));
              clearFieldError('startDate');
            }}
            required
            error={invalidFields.has('startDate')}
            errorMessage={error ?? undefined}
          />
          <InputGroup
            type="date"
            label={t('form.end_date', { ns: 'jobs' })}
            name="job-end-date"
            value={params.endDate ?? ''}
            min={params.startDate || today}
            onChange={event => {
              setParams(current => ({ ...current, endDate: event.target.value || null }));
              clearFieldError('endDate');
            }}
            error={invalidFields.has('endDate')}
            errorMessage={error ?? undefined}
          />
          <InputGroup
            type="date"
            label={t('wizard.quote_submission_deadline', { ns: 'jobs' })}
            name="job-expires-at"
            value={expiresAt}
            min={today}
            max={params.startDate || undefined}
            onChange={event => {
              setExpiresAt(event.target.value);
              clearFieldError('expiresAt');
            }}
            required
            error={invalidFields.has('expiresAt')}
            errorMessage={error ?? undefined}
          />
          <InputGroup
            type="date"
            label={t('wizard.expected_response_date', { ns: 'jobs' })}
            name="job-selection-date"
            value={selectionDate}
            min={expiresAt || today}
            max={params.startDate || undefined}
            onChange={event => {
              setSelectionDate(event.target.value);
              clearFieldError('selectionDate');
            }}
            error={invalidFields.has('selectionDate')}
            errorMessage={error ?? undefined}
          />
        </div>
      )}
      {step === 2 && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-secondary">
            {t('wizard.listing_priority_help', { ns: 'jobs' })}
          </p>
          {loadingListingTypes ? (
            <p>{t('wizard.loading_listing_priorities', { ns: 'jobs' })}</p>
          ) : listingOptions.length === 0 ? (
            <p>{t('wizard.no_listing_priorities', { ns: 'jobs' })}</p>
          ) : (
            <div
              className="grid gap-3 md:grid-cols-3"
              role="group"
              aria-invalid={invalidFields.has('listingType')}
              aria-describedby={
                invalidFields.has('listingType') ? 'job-listing-type-error' : undefined
              }
            >
              {listingOptions.map(option => (
                <button
                  type="button"
                  key={option.id}
                  onClick={() => {
                    setSelectedOption(option);
                    clearFieldError('listingType');
                  }}
                  aria-pressed={selectedOption?.id === option.id}
                  className={`rounded-xl border p-4 text-left ${selectedOption?.id === option.id ? 'border-primary bg-primary/5' : 'border-gray-200'}`}
                >
                  <strong>{option.label}</strong>
                  {selectedOption?.id === option.id && (
                    <span className="mt-2 inline-flex rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-white">
                      {t('wizard.selected_listing_priority', { ns: 'jobs' })}
                    </span>
                  )}
                  <span className="mt-1 block text-sm">{option.description}</span>
                  <span className="mt-2 block text-sm font-semibold">
                    {t('wizard.credit_count', { ns: 'jobs', count: option.cost })}
                  </span>
                </button>
              ))}
            </div>
          )}
          {invalidFields.has('listingType') && (
            <p id="job-listing-type-error" className="text-sm text-red-700">
              {error}
            </p>
          )}
          {isAdministrator && (
            <InputGroup label={t('wizard.create_on_behalf_of', { ns: 'jobs' })}>
              <select
                value={shadowRecruiterId}
                onChange={event => setShadowRecruiterId(event.target.value)}
                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-3"
              >
                <option value="">
                  {loadingShadowRecruiters
                    ? t('wizard.loading_recruiters', { ns: 'jobs' })
                    : t('wizard.no_shadow_recruiter', { ns: 'jobs' })}
                </option>
                {shadowRecruiters.map(recruiter => (
                  <option key={recruiter.id} value={recruiter.id}>
                    {recruiter.company_name} ({recruiter.email})
                  </option>
                ))}
              </select>
            </InputGroup>
          )}
        </div>
      )}
      {step === 3 && serializableParams && (
        <JobPreview
          job={{ ...serializableParams, requiredServices: services }}
          publication={{
            expiresAt,
            selectionDate: selectionDate || null,
            listingType: selectedOption?.label ?? null,
          }}
        />
      )}
      {step === 4 && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
          <h3 className="text-lg font-bold text-secondary">
            {t(isDraftOnly ? 'wizard.ready_to_save_draft' : 'wizard.ready_to_publish', {
              ns: 'jobs',
            })}
          </h3>
          {isDraftOnly ? (
            <p className="mt-2 text-secondary">{t('wizard.admin_draft_help', { ns: 'jobs' })}</p>
          ) : (
            <p className="mt-2 text-secondary">
              {shadowRecruiterId
                ? t('wizard.shadow_publish_help', { ns: 'jobs' })
                : t('wizard.credit_publish_help', {
                    ns: 'jobs',
                    cost: selectedOption?.cost ?? 0,
                    balance: balance ?? 0,
                  })}
            </p>
          )}
          {!isDraftOnly &&
            !shadowRecruiterId &&
            balance !== null &&
            selectedOption &&
            balance < selectedOption.cost && (
              <p className="mt-3 text-red-600">
                {t('wizard.insufficient_credits', { ns: 'jobs' })}
              </p>
            )}
          {!isDraftOnly && !shadowRecruiterId && <WalletSummary outerClassName="mt-5 max-w-md" />}
        </div>
      )}
      {error && (
        <div className="rounded bg-red-50 p-3 text-red-700" role="alert">
          {error}
        </div>
      )}
      <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-between">
        <Button variant="simple-text" type="button" onClick={discard}>
          {t('wizard.actions.discard_draft', { ns: 'jobs' })}
        </Button>
        <div className="flex flex-col gap-3 md:flex-row">
          <Button
            variant="transparent-with-border"
            type="button"
            disabled={step === 0 || submitting}
            onClick={() => {
              setError(null);
              setStep(current => current - 1);
            }}
          >
            {t('wizard.actions.back', { ns: 'jobs' })}
          </Button>
          {step < 4 ? (
            <Button type="button" onClick={next}>
              {t('wizard.actions.next', { ns: 'jobs' })}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={complete}
              disabled={
                submitting ||
                (!isDraftOnly &&
                  !shadowRecruiterId &&
                  Boolean(selectedOption && balance !== null && balance < selectedOption.cost))
              }
            >
              {submitting
                ? t(isDraftOnly ? 'wizard.actions.saving_draft' : 'wizard.actions.publishing', {
                    ns: 'jobs',
                  })
                : t(isDraftOnly ? 'wizard.actions.save_draft' : 'wizard.actions.confirm_publish', {
                    ns: 'jobs',
                  })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateJobWizard;
